import { LitElement, html, css } from 'lit'
import { api } from '../api.js'

// CSAT rating landing page (§7.10). PUBLIC — reached from the tokenized Good/Bad
// links in the solved email, with no session. The token + preselected rating come
// from the query string (?token=…&rating=good|bad); clicking a link records the
// rating immediately, then the page invites an optional comment.
class PageCsat extends LitElement {
  static properties = {
    // 'rate'      — no rating yet, invite one
    // 'done'      — rating recorded, comment invited (they may still change either)
    // 'submitted' — comment sent: terminal, nothing left for them to do here
    _state: { state: true },      // 'loading' | 'rate' | 'done' | 'submitted' | 'invalid'
    _rating: { state: true },
    _comment: { state: true },
    _subject: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; max-width: 32rem; margin: 3rem auto; padding: 0 1rem; }
    .card { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); padding: 2rem; text-align: center; }
    h1 { color: var(--bx-brand); font-size: 1.4rem; margin: 0 0 0.5rem; }
    p { color: var(--bx-muted); margin: 0 0 1.25rem; }
    .choices { display: flex; gap: 1rem; justify-content: center; margin-bottom: 1rem; }
    .choice { font: inherit; font-size: 1.1rem; font-weight: 700; cursor: pointer; padding: 0.8rem 1.4rem;
              border: 1px solid var(--bx-border); border-radius: var(--bx-radius); background: var(--bx-bg); color: var(--bx-ink); }
    .choice.good:hover, .choice.good.sel { background: #16a34a; color: #fff; border-color: transparent; }
    .choice.bad:hover, .choice.bad.sel { background: #dc2626; color: #fff; border-color: transparent; }
    textarea { width: 100%; box-sizing: border-box; font: inherit; padding: 0.6rem; min-height: 90px;
               border: 1px solid var(--bx-border); border-radius: var(--bx-radius); background: var(--bx-bg); color: var(--bx-ink); margin-bottom: 0.75rem; }
    button.primary { font: inherit; font-weight: 700; cursor: pointer; padding: 0.55rem 1.1rem; border: none;
             border-radius: var(--bx-radius); background: var(--bx-accent); color: var(--bx-accent-ink); }
    /* Terminal thank-you: the closing line is the quieter of the two. */
    .lede { color: var(--bx-ink); margin: 0 0 0.5rem; }
    .close { color: var(--bx-muted); margin: 0; font-size: 0.95rem; }
  `

  constructor() {
    super()
    this._state = 'loading'
    this._rating = null
    this._comment = ''
    this._subject = ''
    this._busy = false
  }

  get _token() { return new URLSearchParams(location.search).get('token') }

  connectedCallback() {
    super.connectedCallback()
    this._init()
  }

  async _init() {
    const token = this._token
    if (!token) { this._state = 'invalid'; return }
    const preset = new URLSearchParams(location.search).get('rating')
    try {
      const ctx = await api.get(`/csat/${token}`)
      this._subject = ctx.subject
      this._rating = ctx.rating
      this._comment = ctx.comment || ''
      // A rating in the link records immediately (one click from the email).
      if ((preset === 'good' || preset === 'bad') && ctx.rating !== preset) {
        await this._record(preset)
      } else {
        this._state = ctx.rating ? 'done' : 'rate'
      }
    } catch {
      this._state = 'invalid'
    }
  }

  async _record(rating) {
    this._busy = true
    try {
      const res = await api.post(`/csat/${this._token}`, { rating, comment: this._comment || undefined })
      this._rating = res.rating
      this._state = 'done'
    } catch {
      this._state = 'invalid'
    } finally {
      this._busy = false
    }
  }

  async _saveComment() {
    if (!this._rating) return
    await this._record(this._rating)   // re-submit with the current comment
    // Only on a successful post — a failure leaves _record's 'invalid' state alone.
    // Without this the page re-rendered identically after submitting and gave no sign
    // the comment had gone anywhere. This state is terminal: the rating is saved, the
    // comment is sent, and there is nothing further to do on a page reached from an
    // email with no session behind it.
    if (this._state === 'done') this._state = 'submitted'
  }

  render() {
    if (this._state === 'loading') return html`<div class="card">Loading…</div>`
    if (this._state === 'invalid') return html`<div class="card"><h1>Link expired</h1><p>This rating link is no longer valid.</p></div>`
    if (this._state === 'submitted') {
      return html`
        <div class="card" role="status">
          <h1>Thank you!</h1>
          <p class="lede">Your rating and comment have been sent to the team.</p>
          <p class="close">You can close this window.</p>
        </div>
      `
    }
    return html`
      <div class="card">
        <h1>${this._state === 'done' ? 'Thanks for your feedback!' : 'How did we do?'}</h1>
        <p>${this._subject}</p>
        <div class="choices">
          <button class="choice good ${this._rating === 'good' ? 'sel' : ''}" @click=${() => this._record('good')} ?disabled=${this._busy}>👍 Good</button>
          <button class="choice bad ${this._rating === 'bad' ? 'sel' : ''}" @click=${() => this._record('bad')} ?disabled=${this._busy}>👎 Bad</button>
        </div>
        ${this._rating ? html`
          <textarea placeholder="Anything you'd like to add? (optional)"
            .value=${this._comment} @input=${e => (this._comment = e.target.value)}></textarea>
          <button class="primary" @click=${this._saveComment} ?disabled=${this._busy}>Send comment</button>
        ` : ''}
      </div>
    `
  }
}

customElements.define('page-csat', PageCsat)
